/**
 * Sessões de conversa com os agentes, e o vínculo bot → item do catálogo.
 *
 * Duas seções na mesma aba porque uma explica a outra: conversa sem vínculo
 * não aparece na contagem de nenhum agente, e a única forma de descobrir isso
 * é ver o bot na lista de vínculos esperando alguém apontar.
 *
 * ## O aviso de conteúdo não é decoração
 *
 * Com `TELEMETRY_STORE_CONTENT=false` — o padrão — os turnos chegam sem texto.
 * Sem dizer isso na tela, quem abre uma conversa conclui que ela foi vazia, e
 * o defeito imaginário custa mais tempo que o aviso.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, SessionError, goToSignIn } from '../api.js';
import { notify } from './Toast.jsx';
import { Markdown } from './Markdown.jsx';

/** Data e hora curtas. Valor ausente vira travessão, nunca "Invalid Date". */
function dateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Quanto durou, do primeiro ao último turno. */
function duration(from, to) {
  if (!from || !to) return '—';
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'menos de 1 min';
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${min % 60} min`;
}

/* ── uma conversa, aberta ────────────────────────────────────── */

function ConversationPanel({ conversationId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.admin
      .conversation(conversationId)
      .then((d) => alive && setData(d))
      .catch((err) => {
        if (err instanceof SessionError) return goToSignIn();
        return alive && setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, [conversationId]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel conversation-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conversa-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <h2 id="conversa-titulo" className="panel-title">
            {data?.conversation?.itemName ?? 'Conversa'}
          </h2>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className="panel-body editor-body">
          {!data && !error && <p className="state-message">Carregando…</p>}
          {error && (
            <p className="error-note" role="alert">
              {error}
            </p>
          )}

          {data && (
            <>
              <dl className="facts">
                <div className="fact-row">
                  <dt>Usuário</dt>
                  <dd>{data.conversation.userDisplayName ?? data.conversation.userEmail ?? '—'}</dd>
                </div>
                <div className="fact-row">
                  <dt>Início</dt>
                  <dd>{dateTime(data.conversation.startedAt)}</dd>
                </div>
                <div className="fact-row">
                  <dt>Duração</dt>
                  <dd>
                    {duration(data.conversation.startedAt, data.conversation.lastTurnAt)} ·{' '}
                    {data.conversation.turnCount} turnos
                  </dd>
                </div>
                <div className="fact-row">
                  <dt>Canal</dt>
                  <dd>
                    {data.conversation.channelId ?? '—'}
                    {data.conversation.isTest && <span className="you-tag">teste</span>}
                  </dd>
                </div>
              </dl>

              {!data.storesContent && (
                <div className="locked-note">
                  <strong>O conteúdo das conversas não é guardado.</strong> A plataforma registra
                  quem falou com qual agente, quando e quanto — não o que foi dito. Para ver o
                  texto, `TELEMETRY_STORE_CONTENT` precisa ser ligada, e isso é decisão de
                  Compliance.
                </div>
              )}

              <div className="turns">
                {data.turns.map((t) => (
                  <div className="turn" key={t.eventId}>
                    <div className="turn-time">{dateTime(t.occurredAt)}</div>

                    {t.eventType === 'copilot.conversation.started' ? (
                      <div className="turn-system">Conversa iniciada</div>
                    ) : (
                      <>
                        <div className="turn-side turn-user">
                          <span className="turn-who">Usuário</span>
                          {t.userText ? (
                            <p>{t.userText}</p>
                          ) : (
                            <p className="turn-hidden">
                              {t.userTextLength
                                ? `${t.userTextLength} caracteres, não guardados`
                                : 'sem mensagem'}
                            </p>
                          )}
                        </div>

                        <div className="turn-side turn-bot">
                          <span className="turn-who">Agente</span>
                          {t.botText ? (
                            <Markdown text={t.botText} />
                          ) : (
                            <p className="turn-hidden">
                              {t.botTextLength
                                ? `${t.botTextLength} caracteres, não guardados`
                                : 'sem resposta'}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── vínculos ────────────────────────────────────────────────── */

function Bindings({ items, onBound }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await api.admin.bindings());
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      return notify(err.message);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function bind(b, itemId) {
    setSaving(b.externalId);
    try {
      const r = await api.admin.bind(b.source, b.externalId, itemId || null);
      setData((d) => ({ ...d, bindings: r.bindings }));
      notify(
        r.reprocessed > 0
          ? `Vinculado. ${r.reprocessed} conversa(s) já registrada(s) passaram a contar.`
          : 'Vínculo salvo.',
      );
      onBound?.();
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message);
    } finally {
      setSaving(null);
    }
    return undefined;
  }

  const bindings = data?.bindings ?? [];

  return (
    <div className="list-panel">
      <div className="panel-hd">
        <h2>Agentes vistos na telemetria</h2>
      </div>

      {data && !data.telemetryEnabled && (
        <p className="state-message">
          A recepção de telemetria não está configurada neste ambiente — falta a chave{' '}
          <code>TELEMETRY_KEY</code> no <code>.env</code>.
        </p>
      )}

      {bindings.length === 0 ? (
        <p className="state-message">
          Nenhum agente enviou telemetria ainda. Quando o tópico for publicado num agente, ele
          aparece aqui sozinho.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th scope="col">Agente na origem</th>
                <th scope="col" className="num">
                  Conversas
                </th>
                <th scope="col">Visto por último</th>
                <th scope="col">Item do catálogo</th>
              </tr>
            </thead>
            <tbody>
              {bindings.map((b) => (
                <tr key={`${b.source}:${b.externalId}`}>
                  <th scope="row" className="cell-person">
                    <span className="person-text">
                      <span className="person-name">{b.botName ?? b.externalId}</span>
                      <span className="person-upn">{b.botSchema ?? b.externalId}</span>
                    </span>
                  </th>
                  <td className="num">{b.conversations}</td>
                  <td>
                    <span className="cell-main">{dateTime(b.lastSeenAt)}</span>
                  </td>
                  <td>
                    <select
                      className="input"
                      value={b.itemId ?? ''}
                      disabled={saving === b.externalId}
                      onChange={(e) => bind(b, e.target.value)}
                      aria-label={`Item do catálogo para ${b.botName ?? b.externalId}`}
                    >
                      <option value="">— sem vínculo —</option>
                      {items.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="panel-ft">
        A telemetria identifica o agente por um GUID do Power Platform, que não tem relação com o
        identificador do catálogo. Sem o vínculo, a conversa é registrada mas não conta para nenhum
        item. Vincular depois alcança o que já entrou.
      </div>
    </div>
  );
}

/* ── a aba ───────────────────────────────────────────────────── */

export function AdminConversations({ items, initialItemId = '', initialUserOid = '' }) {
  const [filters, setFilters] = useState({
    itemId: initialItemId,
    userOid: initialUserOid,
    from: '',
    to: '',
    search: '',
    includeTest: false,
  });
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.admin.conversations(filters));
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setLoading(false);
    }
    return undefined;
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (campo) => (e) =>
    setFilters((f) => ({
      ...f,
      [campo]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));

  const conversations = data?.conversations ?? [];

  // O filtro por usuário chega pela URL, vindo do ícone da lista de usuários.
  // Ele NÃO tem campo próprio — filtrar por `oid` num campo de texto seria
  // pedir para a pessoa digitar um GUID. Vira uma etiqueta removível, e o
  // nome sai das próprias conversas, que são todas dele.
  const userName = filters.userOid
    ? (conversations[0]?.userDisplayName ?? conversations[0]?.userEmail ?? filters.userOid)
    : null;

  const active =
    [filters.itemId, filters.userOid, filters.from, filters.to, filters.search].filter(Boolean)
      .length + (filters.includeTest ? 1 : 0);

  function clearAll() {
    setFilters({ itemId: '', userOid: '', from: '', to: '', search: '', includeTest: false });
    // Tira da URL também: sem isto, um F5 traz os filtros de volta e a pessoa
    // conclui que o botão não funcionou.
    window.history.replaceState(null, '', `${window.location.pathname}?aba=conversations`);
  }

  return (
    <>
      <div className="list-panel">
        <div className="filter-row">
          <div className="field">
            <label className="field-label" htmlFor="f-item">
              Agente
            </label>
            <select id="f-item" className="input" value={filters.itemId} onChange={set('itemId')}>
              <option value="">Todos</option>
              {items.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field-label" htmlFor="f-from">
              De
            </label>
            <input
              id="f-from"
              className="input"
              type="date"
              value={filters.from}
              onChange={set('from')}
            />
          </div>

          <div className="field">
            <label className="field-label" htmlFor="f-to">
              Até
            </label>
            <input
              id="f-to"
              className="input"
              type="date"
              value={filters.to}
              onChange={set('to')}
            />
          </div>

          <div className="field field-grow">
            <label className="field-label" htmlFor="f-search">
              Usuário
            </label>
            <input
              id="f-search"
              className="input"
              type="search"
              placeholder="Nome ou e-mail…"
              value={filters.search}
              onChange={set('search')}
            />
          </div>

          <div className="field field-check">
            {/* O `<input>` continua sendo o controle de verdade — só o desenho
                dele é nosso, por `appearance: none`. Trocar por uma <div> com
                `onClick` perderia teclado, leitor de tela e o rótulo clicável. */}
            <label className="switch" htmlFor="f-test">
              <input
                id="f-test"
                type="checkbox"
                checked={filters.includeTest}
                onChange={set('includeTest')}
              />
              <span className="switch-box" aria-hidden="true" />
              <span className="switch-label">Incluir testes</span>
            </label>
          </div>
        </div>

        {active > 0 && (
          <div className="filter-active">
            {filters.userOid && (
              <span className="filter-chip">
                Usuário: <strong>{userName}</strong>
                <button
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, userOid: '' }))}
                  aria-label="Remover o filtro de usuário"
                >
                  ×
                </button>
              </span>
            )}

            <button type="button" className="button ghost clear-filters" onClick={clearAll}>
              Limpar {active === 1 ? 'o filtro' : `os ${active} filtros`}
            </button>
          </div>
        )}

        {loading && <p className="state-message">Carregando…</p>}

        {error && (
          <div className="state-message error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={load}>
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !error && conversations.length === 0 && (
          <p className="state-message">
            Nenhuma conversa registrada com esses filtros. Conversas de teste ficam de fora por
            padrão — marque “Incluir testes” se estiver testando um agente no Copilot Studio.
          </p>
        )}

        {!loading && !error && conversations.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Usuário</th>
                  <th scope="col">Agente</th>
                  <th scope="col">Início</th>
                  <th scope="col">Duração</th>
                  <th scope="col" className="num">
                    Turnos
                  </th>
                  <th scope="col" className="col-action">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {conversations.map((c) => (
                  <tr key={c.conversationId}>
                    <th scope="row" className="cell-person">
                      <span className="person-text">
                        <span className="person-name">
                          {c.userDisplayName ?? 'Anônimo'}
                          {c.isTest && <span className="you-tag">teste</span>}
                        </span>
                        <span className="person-upn">{c.userEmail ?? c.conversationId}</span>
                      </span>
                    </th>
                    <td>{c.itemName ?? <span className="turn-hidden">sem vínculo</span>}</td>
                    <td>
                      <span className="cell-main">{dateTime(c.startedAt)}</span>
                    </td>
                    <td>{duration(c.startedAt, c.lastTurnAt)}</td>
                    <td className="num">{c.turnCount}</td>
                    <td className="col-action">
                      <button
                        type="button"
                        className="button ghost row-action"
                        onClick={() => setOpen(c.conversationId)}
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && !data.storesContent && (
          <div className="panel-ft">
            O conteúdo das conversas não é guardado: a plataforma registra quem falou com qual
            agente, quando e quanto — não o que foi dito.
          </div>
        )}
      </div>

      <Bindings items={items} onBound={load} />

      {open && <ConversationPanel conversationId={open} onClose={() => setOpen(null)} />}
    </>
  );
}

export default AdminConversations;
